import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { IStateResponse } from 'types/common';
import {
  fetchState,
  fetchCountry,
  fetchEquipmentsDropdown,
  fetchStonly,
  fetchTenantInformation,
} from './services';
import { COMMON_QUERY_KEY } from './keys';
import type { ITenant } from 'types/globals';
import type {
  IEquipmentParams,
  IEquipmentWithGroup,
  IEquipmentWithoutGroup,
} from 'types/Equipment/equipment';
import type { IAddressDetail, IAddressGeocodeParams } from 'types/map';
import { useGooglePlaces } from 'hooks/common/GoogleMap';
import { useErrorHandler } from 'hooks/common/useErrorHandler';
import type { IStonlyData } from 'types/StonlyData';
import { AxiosError } from 'axios';

export const useStateQuery = (
  countryId?: string | null,
): UseQueryResult<IStateResponse> =>
  useQuery({
    queryKey: [COMMON_QUERY_KEY.FETCH_STATE, countryId ?? ''],
    queryFn: () => fetchState(countryId ?? undefined),
    enabled: Boolean(countryId),
  });

export const useCountryQuery = (): UseQueryResult<IStateResponse> =>
  useQuery({
    queryKey: [COMMON_QUERY_KEY.FETCH_COUNTRY],
    queryFn: fetchCountry,
  });

export const useStonlyDataQuery = (
  options?: Omit<
    UseQueryOptions<IStonlyData, Error, IStonlyData>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<IStonlyData, Error> =>
  useQuery({
    queryKey: [COMMON_QUERY_KEY.GET_STONLY_DATA],
    queryFn: fetchStonly,
    refetchOnWindowFocus: false,
    retry: false,
    ...options,
  });

export const useTenantInformationQuery = (
  searchText?: string,
  options?: Omit<
    UseQueryOptions<ITenant[], AxiosError, ITenant[]>,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<ITenant[], AxiosError> =>
  useQuery({
    queryKey: [COMMON_QUERY_KEY.GET_TENANT_INFORMATION, searchText ?? ''],
    queryFn: () => fetchTenantInformation(searchText),
    retry: 2,
    ...options,
  });

export const useGetEquipmentsDropdown = <T extends boolean>({
  params,
  options,
}: {
  params: IEquipmentParams & { group_response: T };
  options?: Partial<
    UseQueryOptions<
      T extends true ? IEquipmentWithGroup : IEquipmentWithoutGroup
    >
  >;
}): UseQueryResult<
  T extends true ? IEquipmentWithGroup : IEquipmentWithoutGroup
> =>
  useQuery({
    queryKey: [COMMON_QUERY_KEY.GET_EQUIPMENTS_DROPDOWN, params],
    queryFn: ({ signal }) => fetchEquipmentsDropdown(params, { signal }),
    retry: false,
    ...options,
  });

export const useGetGeocodeAddress = ({
  lat,
  lng,
  enabled = true,
}: IAddressGeocodeParams): UseQueryResult<IAddressDetail | null> => {
  const { getGeocodeByLatLng, getAddressObject } = useGooglePlaces();
  const { handleError } = useErrorHandler();

  return useQuery({
    queryKey: [COMMON_QUERY_KEY.FETCH_GEOCODE_ADDRESS, lat, lng],

    queryFn: async () => {
      const geoCode = await getGeocodeByLatLng(lat, lng);
      return geoCode?.[0] ?? null;
    },

    select: (data): IAddressDetail | null => {
      if (!data) return null;

      try {
        return getAddressObject(data.address_components);
      } catch (error) {
        handleError({
          fallbackMessage: 'Error parsing address object',
          error: error as Error,
        });
        return null;
      }
    },

    enabled: enabled && !!lat && !!lng,
    // staleTime: 1000 * 60 * 60, // 1 hour
    // gcTime: 1000 * 60 * 60 * 24, // 24 hours
    retry: 1,
    refetchOnWindowFocus: false,
  });
};
